import { useEffect, useState, useCallback } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import type { Organization } from "../types/organizations-api.types";
import { OrganizationsService } from "../services/organizationsService";
import {
  getOrganizationStatusStyles,
  getProviderStyles,
} from "../utils/organizationHelpers";
import { OrganizationStatus, Provider } from "../types/organizations-api.types";
import useTranslations from "@/shared/lang/useTranslations";

interface OrganizationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string | null;
}

export const OrganizationDetailModal = ({
  isOpen,
  onClose,
  organizationId,
}: OrganizationDetailModalProps) => {
  const { t } = useTranslations("cards");
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getStatusLabel = (status: OrganizationStatus): string => {
    const statusMap: Record<OrganizationStatus, string> = {
      [OrganizationStatus.ACTIVE]: t("organizations_status_active"),
      [OrganizationStatus.INACTIVE]: t("organizations_status_inactive"),
      [OrganizationStatus.SUSPENDED]: t("organizations_status_suspended"),
      [OrganizationStatus.PENDING]: t("organizations_status_pending"),
    };
    return statusMap[status] || status;
  };

  const getProviderLabel = (provider: Provider | null): string => {
    if (!provider) return t("organizations_provider_na");
    const providerMap: Record<Provider, string> = {
      [Provider.BLOQUE]: t("organizations_provider_bloque"),
      [Provider.CRYPTO_MATE]: t("organizations_provider_crypto_mate"),
    };
    return providerMap[provider] || provider;
  };

  const fetchOrganizationDetail = useCallback(async () => {
    if (!organizationId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await OrganizationsService.getOrganizationById(organizationId);
      setOrganization(response.data);
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : err instanceof Error
          ? err.message
          : t("organization_detail_error_load");
      setError(errorMessage || t("organization_detail_error_load"));
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, t]);

  useEffect(() => {
    if (isOpen && organizationId) {
      fetchOrganizationDetail();
    } else {
      setOrganization(null);
      setError(null);
    }
  }, [isOpen, organizationId, fetchOrganizationDetail]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return t("organization_detail_not_available");
    try {
      const date = new Date(dateString);
      return date.toLocaleString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return t("organization_detail_not_available");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("organization_detail_modal_title")}
      size="lg"
    >
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-green-500" />
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {!isLoading && !error && organization && (
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="bg-white dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-zinc-700 shadow-sm p-6 space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-gray-200 dark:border-zinc-700">
              <div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t("organization_detail_status_label")}
                </span>
                <span className={`ml-2 px-3 py-1 rounded-full text-sm font-semibold ${getOrganizationStatusStyles(organization.status)}`}>
                  {getStatusLabel(organization.status)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-zinc-700 pb-2">
                  {t("organization_detail_section_basic_title")}
                </h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {t("organization_detail_label_name")}
                    </span>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {organization.name}
                    </p>
                  </div>
                  {organization.slug && (
                    <div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {t("organization_detail_label_slug")}
                      </span>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">
                        {organization.slug}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {t("organization_detail_label_email")}
                    </span>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {organization.email}
                    </p>
                  </div>
                  {organization.phone && (
                    <div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {t("organization_detail_label_phone")}
                      </span>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">
                        {organization.phone}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-zinc-700 pb-2">
                  {t("organization_detail_section_additional_title")}
                </h3>
                <div className="space-y-3">
                  {organization.card_provider && (
                    <div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {t("organization_detail_label_card_provider")}
                      </span>
                      <p className="mt-1">
                        <span className={getProviderStyles(organization.card_provider)}>
                          {getProviderLabel(organization.card_provider)}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-zinc-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {organization.created_at && (
                  <div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {t("organization_detail_label_created_at")}
                    </span>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {formatDate(organization.created_at)}
                    </p>
                  </div>
                )}
                {organization.updated_at && (
                  <div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {t("organization_detail_label_updated_at")}
                    </span>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {formatDate(organization.updated_at)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
